import { useState } from 'react';
import handleProductTbhChange from '../function/HandleProducttbhChange';
import handleAddProductTbh from '../function/HandleAddProductTbh';
import handleRemoveProductTbh from '../function/HandleRemoveProductTbh';
import InfoButton from './InfoButton';
import GetProductInfo from '../function/GetProductInfo';
import ValidateProductValue from '../function/ValidateProductValue';
import PdpIcon from './PdpIcon';
import CustomSelect from '../inputs/CustomSelect';
import { useNavigation } from '../function/navigation';
import { ToBaseUnit, UnitConversion } from '../inputs/UnitConversion';
import { getSvlLockedPdpId } from './PdpComponent';

function ProductComponent({
  inputs,
  setInputs,
  options,
  setOptions,
  result,
  productState,
}) {
  const { nav, dispatch } = useNavigation();

  const [hoveredPdpId, setHoveredPdpId] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const productInfo = GetProductInfo(inputs);
  const lockedSvlPdpId =
    inputs.sv_family === 'SV-L' ? getSvlLockedPdpId(inputs.machine_type) : null;
  const isSvlPdpLocked = lockedSvlPdpId !== null;
  const hasVisiblePdpOption = (options = []) =>
    options.length > 1 || (options.length === 1 && options[0].id !== null);
  const showThroughputColumn = inputs.product_flow !== 'mass flow';

  if (inputs.product_tbh === null) return;

  const round2 = (v) => (typeof v === 'number' ? Math.round(v * 100) / 100 : v);

  const renderRectangular = inputs.product_tbh.some(
    (product) => product.product_shape === 'rectangular'
  );
  const renderRound = inputs.product_tbh.some(
    (product) => product.product_shape === 'round'
  );

  const setProductTab = (tab) =>
    dispatch({ type: 'productCurrentPage', payload: { tab: tab } });

  const setShowProduct = (product) =>
    dispatch({ type: 'focusedProduct', payload: product });

  const handleBlur = (index, name, value) => {
    dispatch({ type: 'focusedInput', payload: null });
    handleProductTbhChange(index, name, value, setInputs);
  };

  const renderField = (fieldInfo, product, index, unit_system) => {
    const { field, input, options, shape, unit } = fieldInfo;

    const selectedPdp = product.pdp
      ? inputs.pdp.find((item) => item.id === product.pdp)
      : null;

    let renderInput;
    if (
      (shape === 'rectangular' && !renderRectangular) ||
      (shape === 'round' && !renderRound)
    ) {
      return null;
    }

    if (input === 'select') {
      let optionField;
      if (field === 'pdp') {
        if (!hasVisiblePdpOption(options)) return null;
        optionField = options.map((option) => ({
          id: option.id,
          name: option.type,
        }));
      } else {
        const resolvedOptions = selectedPdp?.shape
          ? [selectedPdp.shape]
          : options;

        if (
          resolvedOptions?.length === 1 &&
          product.product_shape !== resolvedOptions[0]
        ) {
          handleProductTbhChange(
            index,
            'product_shape',
            resolvedOptions[0],
            setInputs
          );
        }

        optionField = resolvedOptions || [];
      }

      renderInput = (
        <div className="input">
          <CustomSelect
            value={product[field] || ''}
            onChange={(value) =>
              handleProductTbhChange(index, field, value, setInputs)
            }
            disabled={field === 'pdp' && isSvlPdpLocked}
            optionChoice={optionField}
          />
        </div>
      );
    } else if (input === 'checkbox') {
      renderInput = (
        <div className="checkbox-container">
          <input
            className="checkbox-input"
            type="checkbox"
            name={field}
            checked={product[field] || false}
            onChange={(e) =>
              handleProductTbhChange(index, field, e.target.checked, setInputs)
            }
            id={`checkbox-${product.name}`}
          />
          <label
            htmlFor={`checkbox-${product.name}`}
            className="checkbox-label"
          ></label>
        </div>
      );
    } else if (input === 'float_field') {
      const { displayedValue, displayedUnit } = UnitConversion(
        product[field],
        unit_system,
        unit
      );

      const { warning } = ValidateProductValue(
        field,
        selectedPdp,
        product[field],
        result,
        displayedUnit,
        product,
        inputs.sv_family
      );

      renderInput = (
        <div className="input">
          <input
            key={`${field}_${index}_${unit_system}`}
            type="number"
            defaultValue={round2(displayedValue) ?? ''}
            onBlur={(e) => {
              const baseValue = ToBaseUnit(e.target.value, displayedUnit);
              handleProductTbhChange(index, field, baseValue, setInputs);
            }}
            onFocus={() =>
              dispatch({ type: 'focusedInput', payload: `${field}_${index}` })
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
          />
          {warning && (
            <InfoButton infoText={warning} type="warning" align="right" />
          )}
        </div>
      );
    } else if (input === 'str_field') {
      renderInput = (
        <div className="input">
          <input
            type="string"
            name={field}
            defaultValue={product[field] || ''}
            onBlur={(e) => handleBlur(index, field, e.target.value)}
            onFocus={() =>
              dispatch({ type: 'focusedInput', payload: `${field}_${index}` })
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
          />
          {[null, 0, ''].includes(product[field]) && (
            <InfoButton
              infoText={'input required'}
              type="warning"
              align="right"
            />
          )}
        </div>
      );
    }

    return (
      <td className="table-value" key={field}>
        {shape && product.product_shape !== shape ? <></> : renderInput}
      </td>
    );
  };

  const checkedPerTab = {
    PDP: null,
    Size: productState.map((p) => ({
      checked: p.size_input !== false && p.size_checked !== false,
      warning: p.size_warnings,
    })),
    Flow: productState.map((p) => ({
      checked: p.flow_input !== false && p.flow_checked !== false,
      warning: p.flow_warnings,
    })),
  };

  const tabHasWarning = (tab) => {
    const items = checkedPerTab[tab];
    if (!items) return false;

    return items.some((item) => item.checked === false);
  };

  return (
    <div>
      {inputs.product_tbh?.length > 0 && (
        <div className="product-table-container">
          <div className="tabs-line">
            <div className="tabs">
              {['PDP', 'Size', 'Flow'].map((tab) => (
                <button
                  key={tab}
                  className={[
                    'tab-button',
                    nav.productCurrentPage.tab === tab ? 'active' : '',
                    tabHasWarning(tab) ? 'warning' : '',
                  ].join(' ')}
                  onClick={() => setProductTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
          <div className="glass product-table">
            <table>
              <thead>
                <tr>
                  <th></th>
                  {productInfo[nav.productCurrentPage.tab]
                    .filter((info) => {
                      if (
                        (info.shape === 'rectangular' &&
                          renderRectangular === false) ||
                        (info.shape === 'round' && renderRound === false)
                      ) {
                        return false;
                      }
                      if (
                        info.field === 'pdp' &&
                        !hasVisiblePdpOption(info.options)
                      ) {
                        return false;
                      }
                      if (
                        info.field === 'throughput' &&
                        !showThroughputColumn
                      ) {
                        return false;
                      }
                      return true;
                    })
                    .map((fieldInfo) => (
                      <th>
                        {fieldInfo.label}
                        {fieldInfo.unit && (
                          <>
                            {' ['}
                            {
                              UnitConversion(
                                null,
                                inputs.unit_system,
                                fieldInfo.unit
                              ).displayedUnit
                            }
                            {']'}
                          </>
                        )}
                      </th>
                    ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {inputs.product_tbh.map((product, index) => (
                  <>
                    <tr key={index}>
                      <td className="pdp-icon-cell">
                        {product.pdp && product.pdp !== 2082 && (
                          <PdpIcon
                            id={`${product.pdp} ${index}`}
                            pdp_id={product.pdp}
                            name={
                              inputs.pdp.find((pdp) => pdp.id === product.pdp)
                                ?.type
                            }
                            hoveredPdpId={hoveredPdpId}
                            setHoveredPdpId={setHoveredPdpId}
                            tooltipSide="left"
                          />
                        )}
                      </td>
                      {productInfo[nav.productCurrentPage.tab]
                        .filter(
                          (info) =>
                            !(
                              info.field === 'throughput' &&
                              !showThroughputColumn
                            )
                        )
                        .map((fieldInfo) =>
                          renderField(
                            fieldInfo,
                            product,
                            index,
                            inputs.unit_system
                          )
                        )}
                      <td className="remove-cell">
                        <button
                          className="remove-button button-warning"
                          onMouseEnter={() => setShowTooltip(index)}
                          onMouseLeave={() => setShowTooltip(null)}
                          onClick={() => {
                            setShowTooltip(null);
                            handleRemoveProductTbh(
                              index,
                              setInputs,
                              nav.focusedProduct,
                              setShowProduct
                            );
                          }}
                        >
                          ×
                        </button>
                        {showTooltip === index && (
                          <div className="tooltip warning right">
                            Remove product
                          </div>
                        )}
                      </td>
                    </tr>
                    {(() => {
                      const tab = nav.productCurrentPage.tab;
                      const warning = checkedPerTab[tab]?.[index]?.warning;

                      if (!warning) return null;

                      return (
                        <tr key={`warning-${index}`} className="warning-row">
                          <td
                            colSpan={productInfo[tab].length + 2}
                            className="warning-cell"
                          >
                            {warning}
                          </td>
                        </tr>
                      );
                    })()}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <button
        className="button-md button-primary"
        onClick={() => handleAddProductTbh(setInputs, setProductTab)}
      >
        <span>+</span> Add a product
      </button>
    </div>
  );
}

export default ProductComponent;
